import React, { useEffect, useRef, useState } from "react";
import { Input, Spin } from "antd";
import { CloseOutlined, MessageOutlined, SendOutlined } from "@ant-design/icons";

import config from "../../config";
import { controller as rootController } from "../../controller";
import "./ftcAssistant.css";

/**
 * "How do I get a breakdown from Manulife?"
 *
 * A launcher pinned to every logged-in page, for super admins and FTC personnel.
 * It answers from the carrier playbooks — the same text shown on the carrier card
 * — so an FTC can ask before they have a patient open, which is when the question
 * usually occurs to them.
 *
 * Visible only to that population. The check here is COSMETIC: the endpoint
 * refuses everyone else regardless, and that is the real boundary. Hiding the
 * launcher just avoids showing people a door they cannot open.
 *
 * Stateless server-side — the transcript lives here and the last few turns ride
 * along with each question. Nothing needs recalling later, and storing FTC
 * questions verbatim would quietly accumulate patient context we have no reason
 * to keep.
 */

function authHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  return user && user.key ? { Authorization: "Token  " + user.key } : {};
}

const GREETING = {
  role: "assistant",
  content:
    "Ask me how to get a breakdown or an estimate from any carrier — the phone "
    + "menu, what they'll ask for, and the quirks people have run into.",
};

export default function FtcAssistant() {
  const [allowed, setAllowed] = useState(false);
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([GREETING]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  // Same role check the sidebar uses: "super_admin", or a role list containing
  // an FTC-Personnel entry.
  useEffect(function () {
    let cancelled = false;
    (async function () {
      try {
        const res = await rootController.checkPracticeAdmin();
        const result = res && res.data && res.data.result;
        if (cancelled) return;
        if (result === "super_admin") { setAllowed(true); return; }
        const roles = Array.isArray(result) ? result : [];
        setAllowed(roles.some(function (name) {
          return typeof name === "string" && name.indexOf("FTC-Personnel") !== -1;
        }));
      } catch (e) {
        if (!cancelled) setAllowed(false);
      }
    })();
    return function () { cancelled = true; };
  }, []);

  useEffect(function () {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy]);

  async function ask() {
    const question = draft.trim();
    if (!question || busy) return;

    const history = turns.filter(function (t) { return t !== GREETING; });
    setTurns(history.concat([{ role: "user", content: question }]));
    setDraft("");
    setBusy(true);
    try {
      const res = await fetch(`${config.apiGateway.URL}/clinics/ftc-assistant/`, {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, authHeader()),
        body: JSON.stringify({ question: question, history: history.slice(-8) }),
      });
      const body = res.ok ? await res.json() : null;
      setTurns(function (prev) {
        return prev.concat([{
          role: "assistant",
          content: (body && body.answer)
            || "I couldn't reach the knowledge base just now. The carrier's phone "
               + "number and portal are still on the policy card.",
          carriers: (body && body.carriers_cited) || [],
        }]);
      });
    } catch (e) {
      setTurns(function (prev) {
        return prev.concat([{
          role: "assistant",
          content: "I couldn't reach the knowledge base just now.",
        }]);
      });
    } finally {
      setBusy(false);
    }
  }

  if (!allowed) return null;

  return (
    <>
      {open ? (
        <div className="fa-panel" role="dialog" aria-label="Carrier help">
          <div className="fa-head">
            <span className="fa-head-title">Carrier help</span>
            <button
              type="button"
              className="fa-close"
              aria-label="Close"
              onClick={function () { setOpen(false); }}
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="fa-body">
            {turns.map(function (turn, i) {
              return (
                <div className={"fa-turn fa-turn--" + turn.role} key={i}>
                  <div className="fa-bubble">{turn.content}</div>
                  {turn.carriers && turn.carriers.length ? (
                    <div className="fa-cited">
                      {turn.carriers.map(function (c) {
                        return <span className="fa-cite" key={c.code}>{c.name}</span>;
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {busy ? (
              <div className="fa-turn fa-turn--assistant">
                <div className="fa-bubble fa-bubble--thinking"><Spin size="small" /></div>
              </div>
            ) : null}
            <div ref={endRef} />
          </div>

          <div className="fa-foot">
            <Input.TextArea
              value={draft}
              onChange={function (e) { setDraft(e.target.value); }}
              onPressEnter={function (e) {
                // Enter sends; Shift+Enter is a newline. Most questions are one line.
                if (!e.shiftKey) { e.preventDefault(); ask(); }
              }}
              placeholder="How do I get a breakdown from Manulife?"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={busy}
            />
            <button
              type="button"
              className="fa-send"
              onClick={ask}
              disabled={busy || !draft.trim()}
              aria-label="Send"
            >
              <SendOutlined />
            </button>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className={"fa-launcher" + (open ? " is-open" : "")}
        onClick={function () { setOpen(!open); }}
        aria-label="Carrier help"
      >
        <MessageOutlined />
      </button>
    </>
  );
}
